import { supabase } from '../lib/supabase'

// Parse UTM parameters from URL
export function getUTMParams(): {
  utm_source?: string
  utm_medium?: string
  utm_campaign?: string
  utm_content?: string
  email_id?: string
} {
  const params = new URLSearchParams(window.location.search)
  return {
    utm_source: params.get('utm_source') || undefined,
    utm_medium: params.get('utm_medium') || undefined,
    utm_campaign: params.get('utm_campaign') || undefined,
    utm_content: params.get('utm_content') || undefined,
    email_id: params.get('eid') || undefined // email queue ID
  }
}

// Check if user came from an email
export function isFromEmail(): boolean {
  const params = getUTMParams()
  return params.utm_source === 'email' || !!params.email_id
}

// Get device info for tracking
function getDeviceInfo(): { device_type: string; browser: string; os: string } {
  const ua = navigator.userAgent
  let browser = 'Unknown'
  let os = 'Unknown'
  let device_type: 'desktop' | 'tablet' | 'mobile' = 'desktop'

  // Detect browser
  if (ua.includes('Firefox')) browser = 'Firefox'
  else if (ua.includes('Chrome') && !ua.includes('Edg')) browser = 'Chrome'
  else if (ua.includes('Safari') && !ua.includes('Chrome')) browser = 'Safari'
  else if (ua.includes('Edg')) browser = 'Edge'

  // Detect OS
  if (ua.includes('Windows')) os = 'Windows'
  else if (ua.includes('Mac')) os = 'macOS'
  else if (ua.includes('Linux')) os = 'Linux'
  else if (ua.includes('Android')) os = 'Android'
  else if (ua.includes('iPhone') || ua.includes('iPad')) os = 'iOS'

  // Detect device type
  const width = window.innerWidth
  if (width < 768) device_type = 'mobile'
  else if (width < 1024) device_type = 'tablet'

  return { device_type, browser, os }
}

// Determine link type from URL path
function getLinkType(path: string): string {
  if (path.includes('/profile/') && path.includes('/issues/')) return 'curation'
  if (path.includes('/profile/')) return 'profile'
  if (path.includes('/product/')) return 'contribution'
  if (path.includes('/brand/')) return 'brand'
  if (path.includes('/issues/')) return 'issue'
  if (path.includes('/unsubscribe')) return 'unsubscribe'
  if (path === '/' || path === '') return 'homepage'
  return 'other'
}

// Track email click
export async function trackEmailClick(sessionId?: string): Promise<void> {
  const params = getUTMParams()

  // Only track if this looks like it came from an email
  if (!isFromEmail()) return

  const deviceInfo = getDeviceInfo()
  const path = window.location.pathname

  try {
    // Get current user if logged in
    const { data: { user } } = await supabase.auth.getUser()

    await supabase.from('email_clicks').insert({
      email_queue_id: params.email_id || null,
      user_id: user?.id || null,
      link_url: window.location.href,
      link_type: getLinkType(path),
      link_id: extractResourceId(path),
      utm_source: params.utm_source,
      utm_medium: params.utm_medium,
      utm_campaign: params.utm_campaign,
      utm_content: params.utm_content,
      session_id: sessionId,
      device_type: deviceInfo.device_type,
      browser: deviceInfo.browser,
      os: deviceInfo.os
    })

    // Clear UTM params from URL to avoid duplicate tracking on navigation
    const url = new URL(window.location.href)
    url.searchParams.delete('utm_source')
    url.searchParams.delete('utm_medium')
    url.searchParams.delete('utm_campaign')
    url.searchParams.delete('utm_content')
    url.searchParams.delete('eid')
    window.history.replaceState({}, '', url.toString())

  } catch (error) {
    console.error('Error tracking email click:', error)
  }
}

// Extract resource ID from path
function extractResourceId(path: string): string | null {
  // Match UUID or slug patterns
  const uuidMatch = path.match(/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i)
  if (uuidMatch) return uuidMatch[0]

  // For profile URLs, extract username
  const profileMatch = path.match(/\/profile\/([^/]+)/)
  if (profileMatch) return profileMatch[1]

  // For brand URLs, extract brand name
  const brandMatch = path.match(/\/brand\/([^/]+)/)
  if (brandMatch) return brandMatch[1]

  return null
}

// Generate email tracking URL
export function generateEmailTrackingUrl(
  baseUrl: string,
  emailQueueId: string,
  campaignName: string,
  linkContent?: string
): string {
  const url = new URL(baseUrl)
  url.searchParams.set('utm_source', 'email')
  url.searchParams.set('utm_medium', 'newsletter')
  url.searchParams.set('utm_campaign', campaignName)
  if (linkContent) {
    url.searchParams.set('utm_content', linkContent)
  }
  url.searchParams.set('eid', emailQueueId)
  return url.toString()
}
